import { type ReactNode, useRef } from 'react';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import { usePhotoUpload } from '@/components/onboarding/use-photo-upload';
import { useAuthStore } from '@/stores/auth';
import { CropModal } from '@/components/upload/crop-modal';
import { SubmitBtn } from './verification-upload-parts';
import { useIgInput } from './use-ig-input';
import { VerificationFields } from './verification-upload-fields';
import { type Lock, useVerificationLock } from '../hooks/use-verification-lock';

const MIN_IG_LEN = 2;

type Ph = ReturnType<typeof usePhotoUpload>;
type Ig = ReturnType<typeof useIgInput>;

type BodyProps = {
  ph: Ph;
  disabled: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  open: () => void;
  ig: Ig;
  onSubmit: () => void;
  lock: Lock;
};

function submitDisabled(p: BodyProps): boolean {
  if (p.disabled) return true;
  const { photoLocked, igLocked } = p.lock;
  if (photoLocked && igLocked) return false;
  if (photoLocked) return p.ig.value.trim().length < MIN_IG_LEN;
  if (igLocked) return !p.ph.hasFile;
  return !p.ph.hasFile || p.ig.value.trim().length < MIN_IG_LEN;
}

function Body(p: BodyProps): ReactNode {
  return (
    <div className="flex w-full flex-col items-center gap-md">
      <VerificationFields {...p} />
      <SubmitBtn
        uploading={p.ph.uploading}
        disabled={submitDisabled(p)}
        onConfirm={p.onSubmit}
        mode={p.lock.mode}
      />
      <CropModal
        src={p.ph.cropSrc}
        onCancel={p.ph.cancelCrop}
        onConfirm={p.ph.commitCrop}
      />
    </div>
  );
}

function makeOnUploaded(navigate: NavigateFunction): () => void {
  return (): void => {
    const acked = !!useAuthStore.getState().user?.rules_acknowledged_at;
    navigate(acked ? '/team/collectors' : '/game-rules');
  };
}

type Props = { disabled: boolean };

export function VerificationUpload({ disabled }: Props): ReactNode {
  const navigate = useNavigate();
  const lock = useVerificationLock();
  const ph = usePhotoUpload(makeOnUploaded(navigate));
  const inputRef = useRef<HTMLInputElement>(null);
  const ig = useIgInput();
  const submit = (): void => {
    if (lock.igLocked) return void ph.upload('');
    const norm = ig.validate();
    if (norm) void ph.upload(norm);
  };
  return (
    <Body
      ph={ph}
      disabled={disabled}
      inputRef={inputRef}
      open={() => inputRef.current?.click()}
      ig={ig}
      onSubmit={submit}
      lock={lock}
    />
  );
}
